import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Integrator } from 'src/app/shared/model/integrator.model';
import { MerchantDetails } from 'src/app/shared/model/merchant-details.model';
import { Role } from 'src/app/shared/model/user-role.enum';
import { User } from 'src/app/shared/model/user.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AuthStateService } from 'src/app/shared/services/auth/auth-state.service';
import { buildBase64Image } from 'src/app/shared/utils/file.service';
import { environment } from 'src/environment/environment';
import { FeatherIconsComponent } from '../../../feather-icons/feather-icons.component';

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss'],
  imports: [FeatherIconsComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class MyAccountComponent implements OnInit {
  #authState = inject(AuthStateService);
  /** The profile menu's look; `group/dd` is the nav item (header.component.ts) whose hover opens the dropdown. */
  protected readonly ui = {
    mediaBody: 'ml-[15px] flex-1 text-right max-md:hidden',
    role: 'mb-0 font-[Roboto,sans-serif] text-[12px] leading-none text-(--theme-body-font-color)',
    roleIcon: 'pl-[5px] align-middle',
    dropdown:
      'invisible absolute top-[57px] right-0 left-[-12px] z-8 w-[160px] translate-y-0 [transform:translateY(30px)] bg-white px-2.5 py-0 opacity-0 ' +
      'shadow-[0_0_20px_rgba(89,102,122,0.1)] [transition:all_0.3s_ease,visibility_0s_0.7s] [transition-delay:0.7s] ' +
      'group-hover/dd:visible group-hover/dd:overflow-hidden group-hover/dd:rounded-[5px] group-hover/dd:opacity-100 group-hover/dd:[transform:translateY(0px)] group-hover/dd:[transition-delay:0s] ' +
      'max-[1366px]:left-auto! max-lg:top-[50px] max-sm:fixed max-sm:top-[60px] max-sm:left-[15px]! max-sm:w-[calc(100vw-30px)] max-[480px]:right-5',
    dropdownItem: 'p-2.5!',
    dropdownIcon: '[&_svg]:mr-2.5 [&_svg]:w-4 [&_svg]:align-bottom [&_svg]:stroke-(--dark-color)',
  } as const;

  navSettings = signal(environment.navSettings);
  logo = signal<SafeUrl>(null);
  name = signal<string>(null);
  userRole = signal<string>(null);
  profileRoute = signal<string>(null);
  constructor(
    public authService: AuthService,
    public router: Router,
    public domSanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {
    effect(() => this.initializeUserByRole(this.#authState.user()));
  }

  ngOnInit(): void {
    this.initializeUserByRole(this.#authState.user());
  }

  private initializeUserByRole(user: User) {
    if (user == null) {
      return;
    }
    const entity = this.#authState.getEntity();
    this.name.set(entity?.name || user.name || `${user.lastname || ''}`);
    switch (user.role) {
      case Role.Admin:
        this.userRole.set(this.translate.instant('PROFILE_MENU.ADMIN'));
        this.profileRoute.set('/profile/admin');
        break;
      case Role.SuperAdmin:
        this.userRole.set(this.translate.instant('PROFILE_MENU.SUPER_ADMIN'));
        this.profileRoute.set('/profile/super-admin');
        break;
      case Role.OrgAdmin:
        this.userRole.set(this.translate.instant('PROFILE_MENU.ORG_ADMIN'));
        this.profileRoute.set('/profile/org-admin');
        break;
      case Role.Merchant:
        this.userRole.set(this.translate.instant('PROFILE_MENU.MERCHANT'));
        this.profileRoute.set('/profile/merchant');
        this.#setTheLogo(entity as MerchantDetails);
        break;
      case Role.Support:
        this.userRole.set(this.translate.instant('PROFILE_MENU.SUPPORT'));
        this.profileRoute.set('/profile/support');
        break;
      case Role.Integrator:
        this.userRole.set(this.translate.instant('PROFILE_MENU.INTEGRATOR'));
        this.profileRoute.set('/profile/integrator');
        this.#setTheLogo(entity as Integrator);
        break;
      case Role.User:
        this.userRole.set(this.translate.instant('PROFILE_MENU.USER'));
        break;
    }
  }

  #setTheLogo(entity: MerchantDetails | Integrator) {
    if (entity?.logoData != null) {
      this.logo.set(this.domSanitizer.bypassSecurityTrustUrl(buildBase64Image(entity.logoData)));
    }
  }

  logOut() {
    this.authService.signOut();
  }
}
